import { useEffect, useState } from "react";
import { api } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Plus, Save, X, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const KINDS = [
  { key: "recruiters", label: "Recruiters" },
  { key: "departments", label: "Departments" },
  { key: "cities", label: "Cities" },
  { key: "countries", label: "Countries" },
  { key: "sources", label: "Sources" },
  { key: "regions", label: "Regions" },
  { key: "entities", label: "Entities" },
  { key: "brands", label: "Brands" },
  { key: "units", label: "Units" },
  { key: "business_units", label: "Business Units" },
  { key: "categories", label: "Categories" },
  { key: "budget_types", label: "Budget Types" },
  { key: "employment_types", label: "Employment Types" },
  { key: "locations", label: "Locations (legacy)" },
];

export default function Masters() {
  const [tab, setTab] = useState("recruiters");
  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Admin</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Master Data</h1>
        <p className="text-sm text-muted-foreground mt-1">Maintain dropdown values used across positions.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-card border border-border h-auto flex-wrap justify-start gap-1 p-1">
          {KINDS.map((k) => (
            <TabsTrigger key={k.key} value={k.key} data-testid={`master-tab-${k.key}`}>{k.label}</TabsTrigger>
          ))}
        </TabsList>
        {KINDS.map((k) => (
          <TabsContent key={k.key} value={k.key} className="mt-4">
            <MasterTable kind={k.key} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function MasterTable({ kind }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");

  const load = async () => {
    const r = await api.get(`/masters/${kind}`);
    setItems(r.data);
  };

  useEffect(() => { load(); }, [kind]);

  const add = async () => {
    if (!name.trim()) return;
    try {
      await api.post(`/masters/${kind}`, { name: name.trim() });
      toast.success("Added");
      setName("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const toggleActive = async (item) => {
    try {
      await api.put(`/masters/${kind}/${item.id}`, { is_active: !item.is_active });
      load();
    } catch { toast.error("Failed"); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/masters/${kind}/${editing.id}`, { name: editName.trim() });
      toast.success("Updated");
      setEditing(null);
      load();
    } catch { toast.error("Failed"); }
  };

  const onDelete = async (it) => {
    try {
      await api.delete(`/masters/${kind}/${it.id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="p-4 flex items-center gap-2 border-b border-border">
        <Input
          placeholder={`Add new ${kind.slice(0, -1)}…`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          data-testid={`master-add-input-${kind}`}
        />
        <Button onClick={add} data-testid={`master-add-btn-${kind}`}>
          <Plus className="w-4 h-4 mr-2" /> Add
        </Button>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/50 text-left text-[11px] uppercase tracking-widest text-muted-foreground">
            <th className="px-4 py-3">Name</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className="border-t border-border hover:bg-muted/30">
              <td className="px-4 py-3">
                {editing?.id === it.id ? (
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8" autoFocus />
                ) : (
                  <span className={!it.is_active ? "text-muted-foreground line-through" : ""}>{it.name}</span>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <Switch checked={it.is_active} onCheckedChange={() => toggleActive(it)} />
                  <span className={`text-xs ${it.is_active ? "text-emerald-700" : "text-muted-foreground"}`}>
                    {it.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
              </td>
              <td className="px-4 py-3 text-right">
                {editing?.id === it.id ? (
                  <>
                    <Button size="sm" variant="ghost" onClick={saveEdit}>
                      <Save className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(it); setEditName(it.name); }}>
                      Edit
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete "{it.name}"?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This removes the value from the master list permanently. Existing positions referencing this value remain unchanged but the value will not be selectable for new positions.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => onDelete(it)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan={3} className="p-8 text-center text-muted-foreground">No entries yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
